import {type ReactNode, useCallback, useState} from 'react'
import {ActionList, ActionMenu, Button, Dialog, Flash, IconButton, Spinner, Textarea} from '@primer/react'
import {GitCommitIcon, GitPullRequestIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ModelPicker} from './ModelPicker'
import styles from './CopilotFixDialog.module.css'

type ApplyChangesMode = 'commit' | 'pull_request'

export interface CopilotFixDialogSubmitOptions {
  createPullRequest: boolean
  instructions: string
  model: string
}

export interface CopilotFixDialogProps {
  title: string
  onClose: () => void
  onSubmit: (options: CopilotFixDialogSubmitOptions) => Promise<void | boolean>
  modelsUrl?: string
  submitDisabled?: boolean
  /** When false, the dialog will not auto-close after a successful onSubmit. Defaults to true. */
  closeOnSubmit?: boolean
  children?: ReactNode
}

export const CopilotFixDialog: React.FC<CopilotFixDialogProps> = ({
  title,
  onClose,
  onSubmit,
  modelsUrl,
  submitDisabled = false,
  closeOnSubmit = true,
  children,
}) => {
  const [applyMode, setApplyMode] = useState<ApplyChangesMode>('commit')
  const [isFixing, setIsFixing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const [selectedModel, setSelectedModel] = useState('auto')

  const applyModeLabel = applyMode === 'commit' ? 'Fix and commit' : 'Fix and open pull request'
  const ApplyModeIcon = applyMode === 'commit' ? GitCommitIcon : GitPullRequestIcon

  const handleSubmit = useCallback(async () => {
    setIsFixing(true)
    setErrorMessage('')
    try {
      const result = await onSubmit({
        createPullRequest: applyMode === 'pull_request',
        instructions: additionalInstructions.trim(),
        model: selectedModel,
      })
      if (closeOnSubmit && result !== false) {
        onClose()
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.')
    } finally {
      setIsFixing(false)
    }
  }, [onSubmit, applyMode, additionalInstructions, selectedModel, closeOnSubmit, onClose])

  return (
    <Dialog onClose={onClose} title={title} width="xlarge">
      <Dialog.Body>
        {errorMessage && (
          <Flash variant="danger" className={styles.errorBanner} role="alert">
            {errorMessage}
          </Flash>
        )}
        <div className={styles.textareaContainer}>
          <Textarea
            className={styles.instructionsTextarea}
            aria-label="Additional instructions"
            placeholder="Add additional instructions..."
            value={additionalInstructions}
            onChange={e => setAdditionalInstructions(e.target.value)}
            rows={4}
            resize="none"
          />
          <div className={styles.textareaToolbar}>
            {modelsUrl && <ModelPicker modelsUrl={modelsUrl} onModelChange={setSelectedModel} />}
            <div className={styles.splitButton}>
              <Button
                size="small"
                variant="invisible"
                className={`${styles.contributionMethodButton}${submitDisabled ? ` ${styles.clearVariant}` : ''}`}
                leadingVisual={ApplyModeIcon}
                onClick={handleSubmit}
                disabled={isFixing || submitDisabled}
              >
                {isFixing ? (
                  <>
                    <Spinner size="small" /> Fixing…
                  </>
                ) : (
                  applyModeLabel
                )}
              </Button>
              <ActionMenu>
                <ActionMenu.Anchor>
                  <IconButton
                    size="small"
                    variant="invisible"
                    className={`${styles.contributionMethodDropdown}${submitDisabled ? ` ${styles.clearVariant}` : ''}`}
                    icon={TriangleDownIcon}
                    aria-label="Change contribution method"
                    disabled={isFixing}
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay width="medium">
                  <ActionList selectionVariant="single">
                    <ActionList.Item selected={applyMode === 'commit'} onSelect={() => setApplyMode('commit')}>
                      <ActionList.LeadingVisual>
                        <GitCommitIcon size={16} />
                      </ActionList.LeadingVisual>
                      Commit to this pull request
                      <ActionList.Description variant="block">
                        Copilot will push a commit directly to this PR&apos;s branch.
                      </ActionList.Description>
                    </ActionList.Item>
                    <ActionList.Item
                      selected={applyMode === 'pull_request'}
                      onSelect={() => setApplyMode('pull_request')}
                    >
                      <ActionList.LeadingVisual>
                        <GitPullRequestIcon size={16} />
                      </ActionList.LeadingVisual>
                      Open a new pull request
                      <ActionList.Description variant="block">
                        Copilot will create a new pull request with the changes.
                      </ActionList.Description>
                    </ActionList.Item>
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </div>
          </div>
        </div>
        {children}
      </Dialog.Body>
    </Dialog>
  )
}
